import { useState, useRef, useMemo, useCallback, useEffect, memo } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core';
import PageContainer from '../../shared/pageContainer';
import TextBoxWithTooltip from '../../shared/textBoxWithTooltip';
import SurveySummaryInfo from '../../shared/surveySummaryInfo';
import SurveySummaryDescription from '../../shared/surveySummaryDescription';
import SurveySummaryReviews from '../../shared/surveySummaryReviews';
import SurveySummaryComment from '../../shared/surveySummaryComment';
import AttributeReviewReport from '../../shared/attributeReviewReport';
import QuestionnaireReviewReport from '../../shared/questionnaireReviewReport';
import ReviewCommentsDrawer from '../../shared/reviewCommentsDrawer';
import NpsReviewReport from '../../shared/npsReviewReport';
import { APP_PAGES } from '../../../constants/pages';
import { SURVEY_TYPES } from '../../../constants/survey';
import { SURVEY_STATUS_TYPES } from '../../../constants/statuses';
import { PERMISSION_CODES } from '../../../constants/permissions';
import { sticky } from '../../../constants/helperCssRules';
import { surveySummarySelector } from '../../../store/selectors/surveySummarySelector';
import {
  getSurveySummary,
  clearSurveySummary,
} from '../../../store/modules/surveySummary';
import { transformSurveySummaryData } from '../../../utility/surveySummary';
import {
  prepareSurveyTypes,
  hasReviewCommentsCheck,
  prepareReviewComments,
  prepareReviewCommentsBySelectedView,
  prepareCommentsByQuestion,
} from '../../../utility/survey';
import {
  isArrayEmpty,
  getItemById,
  isObjectEmpty,
} from '../../../utility/helpers';
import { hasAuthPermissionCheck } from '../../../utility/permissions';
import { useTranslations } from '../../../utility/useTranslations';
import { usePreviousValue } from '../../../utility/hooks';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  title: {
    maxWidth: 'calc(100% - 260px)',
    width: '100%',
  },
  divider: {
    paddingTop: spacing(8),
    ...sticky(primary.white, 105),
  },
  reviews: {
    marginTop: spacing(4),
  },
  description: {
    marginTop: spacing(6),
    marginBottom: spacing(5),
  },
  summary: {
    marginTop: spacing(9),
  },
  review: {
    marginBottom: spacing(4),
    '&:last-of-type': {
      marginBottom: 0,
    },
  },
}));

const SurveySummaryDetailsPage = ({
  dispatch,
  auth,
  accessControl,
  organizationSettings,
  ...rest
}) => {
  const classes = useStyles();
  const translations = useTranslations(APP_PAGES.SURVEY_SUMMARY_DETAILS);
  const { summaryId } = useParams();
  const reviewRef = useRef({});
  const pageScrollRef = useRef(null);
  const defaultPageOffset = 137; // Header height + divider height
  const defaultReviewOffset = 48; // Height of collapsed review

  const [isLoading, setIsLoading] = useState(true);
  const [reviewId, setReviewId] = useState(null);
  const [textReplyQuestionId, setTextReplyQuestionId] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState({});
  const [isManagingView, setIsManagingView] = useState(false);
  const [isReviewCommentsDrawerOpened, setIsReviewCommentsDrawerOpened] =
    useState(false);
  const [selectedCommentator, setSelectedCommentator] = useState({});
  const [selectedQuestionCommentsId, setSelectedQuestionCommentsId] =
    useState(null);
  const [isViewAllComments, setIsViewAllComments] = useState(false);

  const previousReviewId = usePreviousValue(reviewId);

  const surveySummary = useSelector(surveySummarySelector);
  const report = useMemo(
    () => transformSurveySummaryData(surveySummary, isManagingView),
    [surveySummary, isManagingView]
  );
  const {
    title,
    survey,
    description,
    reviews = [],
    questionnaires = [],
    isAnonymizedReport,
  } = report;

  const surveyTypes = prepareSurveyTypes(translations);
  const isTeamReview = survey?.type === SURVEY_TYPES.TEAM;
  const isNpsReview = [SURVEY_TYPES.ENPS, SURVEY_TYPES.NPS].includes(
    survey?.type
  );
  const isQuestionnaireReview = survey?.questionnaire !== null;
  const canManage = hasAuthPermissionCheck(
    PERMISSION_CODES.REPORTS_SUMMARY_MANAGE,
    accessControl
  );

  const hasSharedResults =
    !isArrayEmpty(reviews) || !isArrayEmpty(questionnaires);
  const reviewCommentsData = useMemo(() => {
    const initialComments = { reviewCommentators: [], allComments: [] };

    if (reviewId && !isNpsReview) {
      const currentReview = getItemById(reviews, reviewId);

      if (
        !isObjectEmpty(currentReview.replies) &&
        hasReviewCommentsCheck(reviewId, survey.attributes)
      ) {
        return prepareReviewComments(
          translations.review.neutral.notApplicable,
          currentReview.replies,
          {
            isAnonymous:
              survey.isAnonymous || (isAnonymizedReport && !isManagingView),
            type: survey.type,
            reviewers: survey?.reviewers,
          },
          currentReview
        );
      }

      return initialComments;
    }

    return initialComments;
  }, [
    translations,
    reviewId,
    reviews,
    survey,
    isAnonymizedReport,
    isManagingView,
    isNpsReview,
  ]);
  const reviewCommentsByQuestion = useMemo(() => {
    return prepareCommentsByQuestion(
      reviewCommentsData.allComments,
      selectedUsers[reviewId]
    );
  }, [reviewId, reviewCommentsData, selectedUsers]);
  const reviewCommentsBySelectedView = useMemo(
    () =>
      prepareReviewCommentsBySelectedView(
        reviewCommentsData.allComments,
        reviewCommentsByQuestion,
        selectedCommentator,
        selectedQuestionCommentsId,
        isViewAllComments,
        isTeamReview
      ),
    [
      reviewCommentsData,
      reviewCommentsByQuestion,
      selectedCommentator,
      selectedQuestionCommentsId,
      isViewAllComments,
      isTeamReview,
    ]
  );

  const getReportData = useCallback(async () => {
    await getSurveySummary(dispatch, summaryId);

    setIsLoading(false);
  }, [dispatch, summaryId]);

  const hadleReviewScroll = useCallback(() => {
    if (previousReviewId !== reviewId) {
      const prevReview =
        reviewRef?.current?.[previousReviewId]?.getBoundingClientRect();
      const prevReviewOffset =
        reviewRef?.current?.[previousReviewId]?.offsetTop;
      const currReviewOffset = reviewRef?.current?.[reviewId]?.offsetTop;

      if (prevReview && prevReviewOffset < currReviewOffset) {
        setTimeout(() => {
          pageScrollRef?.current?.scrollTo({
            top:
              currReviewOffset -
              prevReview.height -
              defaultPageOffset +
              defaultReviewOffset,
            behavior: 'smooth',
          });
        }, 300);
      } else {
        setTimeout(() => {
          pageScrollRef?.current?.scrollTo({
            top: currReviewOffset - defaultPageOffset,
            behavior: 'smooth',
          });
        }, 300);
      }
    }
  }, [previousReviewId, reviewId]);

  const handleViewReport = useCallback(
    review => async () => {
      if (review.id !== reviewId) {
        if (
          reviewRef?.current?.[reviewId] &&
          reviewRef?.current?.[reviewId]?.offsetTop <
            reviewRef?.current?.[review.id]?.offsetTop
        ) {
          pageScrollRef?.current?.scrollTo({
            top: 0,
          });
        }

        setReviewId(review.id);
        setTextReplyQuestionId(null);
      } else {
        setReviewId(null);
        setTextReplyQuestionId(null);
      }
    },
    [reviewId]
  );

  const onUserSelect = useCallback(
    currentReviewId => (selectedUserId, isSelected) => {
      if (selectedUsers[currentReviewId]) {
        if (isSelected) {
          return setSelectedUsers(prevSelectedUsers => ({
            ...prevSelectedUsers,
            [currentReviewId]: prevSelectedUsers[currentReviewId].filter(
              userId => userId !== selectedUserId
            ),
          }));
        }

        return setSelectedUsers(prevSelectedUsers => ({
          ...prevSelectedUsers,
          [currentReviewId]: [
            ...prevSelectedUsers[currentReviewId],
            selectedUserId,
          ],
        }));
      }

      setSelectedUsers(prevSelectedUsers => ({
        ...prevSelectedUsers,
        [currentReviewId]: [selectedUserId],
      }));
    },
    [selectedUsers]
  );

  const onClearSelectedUsers = useCallback(
    currentId => () => {
      setSelectedUsers(prevSelectedUsers => {
        return Object.keys(prevSelectedUsers).reduce(
          (acc, selectedKey) => ({
            ...acc,
            ...(+selectedKey !== currentId
              ? { [selectedKey]: [...prevSelectedUsers[selectedKey]] }
              : {}),
          }),
          {}
        );
      });
    },
    []
  );

  const handleViewTextReply = questionId => e => {
    e.stopPropagation();

    setTextReplyQuestionId(prevTextReplyQuestionId =>
      prevTextReplyQuestionId !== questionId ? questionId : null
    );
  };

  const handleChangeManagingView = updatedView => {
    setSelectedUsers({});
    setIsManagingView(!updatedView);
  };

  const handleViewAllComents = e => {
    e.stopPropagation();
    setIsReviewCommentsDrawerOpened(true);
    setIsViewAllComments(true);
  };

  const handleViewReviewerComments = userId => {
    setSelectedCommentator(
      getItemById(reviewCommentsData.reviewCommentators, userId)
    );
    setIsReviewCommentsDrawerOpened(true);
  };

  const handleViewQuestionComments = questionId => {
    setSelectedQuestionCommentsId(questionId);
    setIsReviewCommentsDrawerOpened(true);
  };

  const handleCloseReviewCommentsDrawer = e => {
    e.stopPropagation();
    setIsReviewCommentsDrawerOpened(false);
    setSelectedCommentator({});
    setIsViewAllComments(false);
    setSelectedQuestionCommentsId(null);
  };

  useEffect(() => {
    getReportData();

    return () => {
      dispatch(clearSurveySummary());
    };
  }, [dispatch, getReportData]);

  const renderReportHeader = () => {
    return (
      <div className={classes.title}>
        <TextBoxWithTooltip variant="h1" text={title} />
      </div>
    );
  };

  return (
    !isLoading && (
      <PageContainer
        {...rest}
        translations={translations}
        auth={auth}
        accessControl={accessControl}
        organizationSettings={organizationSettings}
        headerProps={{
          renderTitle: renderReportHeader,
        }}
        shouldPassProps={false}
        isFullWidthContent
        pageScrollRef={pageScrollRef}
      >
        <div className={classes.divider} />
        <SurveySummaryInfo
          translations={translations.info}
          report={report}
          canManage={canManage}
          isManagingView={isManagingView}
          hasSharedResults={hasSharedResults}
          onChangeView={handleChangeManagingView}
        />
        <SurveySummaryDescription
          className={classes.description}
          translations={translations.description}
          surveyTypes={surveyTypes}
          report={report}
        />
        {hasSharedResults ? (
          <>
            {reviews.map(review => (
              <div
                key={`review_${review.id}`}
                ref={el => {
                  reviewRef.current[review.id] = el;
                }}
                className={classes.review}
              >
                {isNpsReview ? (
                  <NpsReviewReport
                    translations={translations.review}
                    surveyType={survey.type}
                    review={review}
                    isCompleted={
                      survey?.status === SURVEY_STATUS_TYPES.COMPLETED
                    }
                    shouldDisableIndividualScores
                    isSummaryView
                  />
                ) : (
                  <AttributeReviewReport
                    translations={translations.review}
                    review={review}
                    reviewComments={reviewCommentsByQuestion}
                    selectedUsers={selectedUsers[review.id]}
                    commentators={reviewCommentsData.reviewCommentators}
                    isExpanded={reviewId === review.id}
                    isTeamReview={isTeamReview}
                    hasViewAllComments={
                      survey.isAnonymous ||
                      (isAnonymizedReport && !isManagingView)
                    }
                    isCompleted={
                      survey?.status === SURVEY_STATUS_TYPES.COMPLETED
                    }
                    textReplyQuestionId={textReplyQuestionId}
                    hasComments={hasReviewCommentsCheck(
                      review.id,
                      survey.attributes
                    )}
                    onUserSelect={onUserSelect(review.id)}
                    onClearSelectedUsers={onClearSelectedUsers(review.id)}
                    onViewIndividualScores={() => {}}
                    onViewTextReply={handleViewTextReply}
                    onViewReport={handleViewReport(review)}
                    onScrollInToView={hadleReviewScroll}
                    onViewReviewerComments={handleViewReviewerComments}
                    onViewAllComments={handleViewAllComents}
                    onViewQuestionComments={handleViewQuestionComments}
                    isReadOnlyFeedback
                    shouldDisableIndividualScores
                  />
                )}
              </div>
            ))}
            {questionnaires.map(questionnaire => (
              <div
                key={`questionnaire_${questionnaire.id}`}
                className={classes.review}
              >
                <QuestionnaireReviewReport
                  translations={translations.review}
                  questionnaire={questionnaire}
                  selectedUsers={selectedUsers[questionnaire.id]}
                  reviewersCount={survey?.reviewers?.length}
                  isTeamReview={isTeamReview}
                  isCompleted={survey?.status === SURVEY_STATUS_TYPES.COMPLETED}
                  textReplyQuestionId={textReplyQuestionId}
                  onUserSelect={onUserSelect(questionnaire.id)}
                  onClearSelectedUsers={onClearSelectedUsers(questionnaire.id)}
                  onViewTextReply={handleViewTextReply}
                />
              </div>
            ))}
          </>
        ) : (
          <SurveySummaryReviews
            className={classes.reviews}
            translations={translations.reviews}
            isQuestionnaireReview={isQuestionnaireReview}
            survey={survey}
          />
        )}
        <ReviewCommentsDrawer
          translations={translations.reviewComments}
          isOpened={isReviewCommentsDrawerOpened}
          comments={reviewCommentsBySelectedView}
          commentator={selectedCommentator}
          onClose={handleCloseReviewCommentsDrawer}
          isViewAll={isViewAllComments}
          hasCancelButton
        />
        <SurveySummaryComment
          className={classes.summary}
          title={translations.summaryTitle}
          comment={description}
        />
      </PageContainer>
    )
  );
};

SurveySummaryDetailsPage.propTypes = {
  auth: PropTypes.object.isRequired,
  accessControl: PropTypes.object.isRequired,
  organizationSettings: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
};

export default memo(SurveySummaryDetailsPage);
